import {Button, Modal, Paper, Select, TextInput} from "@mantine/core";
import {useMemo, useState} from "react";
import {Row, Col} from "../../components/Flex/Flex";
import Label from "../../components/Label/Label.tsx";
import NumberSelector from "./NumberSelector.tsx";
import {HyperlanePreset} from "./types.ts";
import {ShipType} from "../../types/swc.types.ts";
import {FloppyDiskIcon, TrashIcon} from "@phosphor-icons/react";

interface PresetModalProps {
  opened: boolean;
  onClose: () => void;
  presets: HyperlanePreset[];
  defaultPresetId: string | null;
  ships: ShipType[];
  onCreate: (payload: {name: string; skill: number; hyper: number; sublight: number}) => void;
  onRemove: (id: string) => void;
  onSetDefault: (id: string) => void;
}

export default function PresetModal({
  opened,
  onClose,
  presets,
  defaultPresetId,
  ships,
  onCreate,
  onRemove,
  onSetDefault,
}: PresetModalProps) {
  const [newPresetName, setNewPresetName] = useState<string>('');
  const [newPresetSkill, setNewPresetSkill] = useState<number>(0);
  const [newPresetHyper, setNewPresetHyper] = useState<number>(1);
  const [newPresetSublight, setNewPresetSublight] = useState<number>(10);
  const [newPresetShipId, setNewPresetShipId] = useState<string | null>(null);

  const shipOptions = useMemo(() => ships.filter(s => s.speed.hyper > 0).map(s => ({value: s.id, label: s.name})), [ships]);

  const createPreset = () => {
    if (!newPresetName) {
      return;
    }
    onCreate({
      name: newPresetName,
      skill: newPresetSkill,
      hyper: newPresetHyper,
      sublight: newPresetSublight,
    });
    setNewPresetName('');
    setNewPresetSkill(0);
    setNewPresetHyper(1);
    setNewPresetSublight(10);
    setNewPresetShipId(null);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Manage presets" size="lg">
      <Row align="end" mb={2}>
        <Col grow={1}>
          <Label mt={0}>Preset Name</Label>
          <TextInput
            value={newPresetName}
            onChange={(e) => setNewPresetName(e.target.value)}
            placeholder="Preset name"
          />
        </Col>
        <Col>
          <Label mt={0}>Ship</Label>
          <Select
            value={newPresetShipId}
            data={shipOptions}
            onChange={(id) => {
              setNewPresetShipId(id);
              const ship = ships.find(s => s.id === id);
              if (ship) {
                setNewPresetHyper(ship.speed.hyper || 1);
                setNewPresetSublight(ship.speed.sublight || 1);
              }
            }}
            placeholder="Load ship stats"
            searchable
          />
        </Col>
        <Col>
          <Label mt={0}>Pilot Skill</Label>
          <NumberSelector min={0} max={5} value={newPresetSkill} onChange={next => setNewPresetSkill(next)}/>
        </Col>
        <Col>
          <Label mt={0}>Hyper speed</Label>
          <NumberSelector min={1} max={15} value={newPresetHyper} onChange={next => {
            setNewPresetShipId(null);
            setNewPresetHyper(next)
          }}/>
        </Col>
        <Col>
          <Label mt={0}>Sublight speed</Label>
          <NumberSelector min={0} max={300} step={5} value={newPresetSublight} onChange={next => {
            setNewPresetShipId(null);
            setNewPresetSublight(next)
          }}/>
        </Col>
        <Col>
          <Button leftSection={<FloppyDiskIcon size={16}/>} onClick={createPreset} disabled={!newPresetName}>Create preset</Button>
        </Col>
      </Row>

      <Paper>
        {presets.length === 0 ? (
          <div>No presets yet.</div>
        ) : (
          presets.map(preset => (
            <Row key={preset.id} align="center" mb={2}>
              <Col grow={1}>
                <b>{preset.name}</b><br/>
                <small>Skill {preset.skill} • Hyper {preset.hyper} • Sublight {preset.sublight}</small>
              </Col>
              <Col>
                <Button
                  variant="light"
                  onClick={() => onSetDefault(preset.id)}
                  disabled={defaultPresetId === preset.id}
                >
                  {defaultPresetId === preset.id ? 'Default' : 'Set default'}
                </Button>
              </Col>
              <Col>
                <Button variant="light" leftSection={<TrashIcon size={16}/>} onClick={() => onRemove(preset.id)}>
                  Delete
                </Button>
              </Col>
            </Row>
          ))
        )}
      </Paper>
    </Modal>
  );
}
